import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { HistoryEvent, HistoryFilters } from '../../types/models';

interface HistoryState {
  events: HistoryEvent[];
  filteredEvents: HistoryEvent[];
  filters: HistoryFilters;
  loading: boolean;
  error: string | null;
}

const initialFilters: HistoryFilters = {
  searchTerm: '',
  actionType: 'all',
};

const applyFilters = (events: HistoryEvent[], filters: HistoryFilters): HistoryEvent[] => {
  let filtered = [...events];

  if (filters.actionType === 'check-in') {
    filtered = filtered.filter(e => !e.checkedOut);
  } else if (filters.actionType === 'check-out') {
    filtered = filtered.filter(e => e.checkedOut);
  }

  if (filters.dateFrom) {
    filtered = filtered.filter(e => e.eventDate >= filters.dateFrom!);
  }
  if (filters.dateTo) {
    // Add a full day to dateTo so it's inclusive
    filtered = filtered.filter(e => e.eventDate <= filters.dateTo! + 86400);
  }

  if (filters.searchTerm) {
    const term = filters.searchTerm.toLowerCase();
    filtered = filtered.filter(e =>
      (e.itemName && e.itemName.toLowerCase().includes(term)) ||
      e.userName.toLowerCase().includes(term) ||
      (e.notes && e.notes.toLowerCase().includes(term)) ||
      e.itemId.toLowerCase().includes(term)
    );
  }

  if (filters.userName) {
    const name = filters.userName.toLowerCase();
    filtered = filtered.filter(e => e.userName.toLowerCase().includes(name));
  }

  return filtered;
};

const initialState: HistoryState = {
  events: [],
  filteredEvents: [],
  filters: initialFilters,
  loading: false,
  error: null,
};

const historySlice = createSlice({
  name: 'history',
  initialState,
  reducers: {
    setEvents(state, action: PayloadAction<HistoryEvent[]>) {
      state.events = action.payload;
      state.filteredEvents = applyFilters(action.payload, state.filters);
      state.loading = false;
    },
    setHistoryFilters(state, action: PayloadAction<Partial<HistoryFilters>>) {
      state.filters = { ...state.filters, ...action.payload };
      state.filteredEvents = applyFilters(state.events, state.filters);
    },
    clearHistoryFilters(state) {
      state.filters = initialFilters;
      state.filteredEvents = applyFilters(state.events, initialFilters);
    },
    setHistoryLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setHistoryError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
      state.loading = false;
    },
  },
});

export const {
  setEvents,
  setHistoryFilters,
  clearHistoryFilters,
  setHistoryLoading,
  setHistoryError,
} = historySlice.actions;

export default historySlice.reducer;
